/*
 *  -----------------------------------------------------------------------
 *    <copyright file="password-requirements-warning.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect } from 'react';

import { useTranslation } from 'hooks';
import DialogWarning from 'views/components/core/dialog-information-box/dialog-information-box';

const PasswordRequirementsWarning = ({ security, password, onPasswordValidate }: any) => {
    const { t } = useTranslation();
    const tick: string = '✔';
    const cross: string = '❌';

    const minPasswordLengthNum: number = security?.minPasswordLength || 0;
    const minSpecialCharCountNum: number = security?.minSpecialCharCount || 0;
    const minLowercaseCountNum: number = security?.minLowercaseCount || 0;
    const minUppercaseCountNum: number = security?.minUppercaseCount || 0;
    const minNumberCountNum: number = security?.minNumberCount || 0;

    const passLengthSymbol: any =
        minPasswordLengthNum === 0 || undefined ? true : password.length >= minPasswordLengthNum ? tick : cross;
    const specialCharSymbol: any =
        minSpecialCharCountNum === 0 || undefined
            ? true
            : password.replace(/[^\W]/g, '').length >= minSpecialCharCountNum
            ? tick
            : cross;
    const numberSymbol: any =
        minNumberCountNum === 0 || undefined
            ? true
            : password.replace(/[^0-9]/g, '').length >= minNumberCountNum
            ? tick
            : cross;
    const lowerUpperSymbol: any =
        (minLowercaseCountNum === 0 || undefined) && (minUppercaseCountNum === 0 || undefined)
            ? true
            : password.replace(/[^A-Z]/g, '').length >= minUppercaseCountNum &&
              password.replace(/[^a-z]/g, '').length >= minLowercaseCountNum
            ? tick
            : cross;
    const lowerSymbol: any =
        minLowercaseCountNum === 0 || undefined
            ? true
            : password.replace(/[^a-z]/g, '').length >= minLowercaseCountNum
            ? tick
            : cross;
    const upperSymbol: any =
        minUppercaseCountNum === 0 || undefined
            ? true
            : password.replace(/[^A-Z]/g, '').length >= minUppercaseCountNum
            ? tick
            : cross;

    useEffect(() => {
        onPasswordValidate(() => {
            const fields = [
                passLengthSymbol,
                specialCharSymbol,
                numberSymbol,
                lowerUpperSymbol,
                lowerSymbol,
                upperSymbol,
            ];
            return fields.includes(cross || false);
        });
    }, []);

    useEffect(() => {
        onPasswordValidate(() => {
            const fields = [
                passLengthSymbol,
                specialCharSymbol,
                numberSymbol,
                lowerUpperSymbol,
                lowerSymbol,
                upperSymbol,
            ];
            return fields.includes(cross || false);
        });
    }, [password, security]);

    const minPasswordLength: any = minPasswordLengthNum
        ? t('uam:minPasswordLengthHtml', { minPasswordLengthNum, passLengthSymbol })
        : null;
    const minSpecialCharCount: any = minSpecialCharCountNum
        ? t('uam:minSpecialCharCountHtml', { minSpecialCharCountNum, specialCharSymbol })
        : null;
    const minNumberCount: string | null = minNumberCountNum
        ? t('uam:minNumberCountHtml', { minNumberCountNum, numberSymbol })
        : null;
    const minLowerUppercaseCount: any =
        minLowercaseCountNum && minUppercaseCountNum
            ? t('uam:minLowerUppercaseCountHtml', { minLowercaseCountNum, minUppercaseCountNum, lowerUpperSymbol })
            : minLowercaseCountNum && !minUppercaseCountNum
            ? t('uam:minLowercaseCountHtml', { minLowercaseCountNum, lowerSymbol })
            : !minLowercaseCountNum && minUppercaseCountNum
            ? t('uam:minUppercaseCountHtml', { minUppercaseCountNum, upperSymbol })
            : null;

    const securitySettings = {
        interpolation: { escapeValue: false },
        passwordRequirements: {
            minLowerUppercaseCount,
            minPasswordLength,
            minSpecialCharCount,
            minNumberCount,
        },
    };

    return <DialogWarning topText={t('uam:updatePasswordWarningHtml', { ...securitySettings })} />;
};

export default PasswordRequirementsWarning;
